import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Pencil, Lock } from "lucide-react";

const UMT_LABEL = {
  RECIPE: "Рецепт",
  NOTE: "Заметка",
  ADAPTATION: "Адаптация",
  VEGAN: "Веганская версия",
  PREP: "Заготовка",
  OTHER: "Другое",
};

function looksLikeHtml(s) {
  return /^\s*<(?:p|h[1-6]|ul|ol|blockquote|hr|img|figure|div|span|strong|em|br|a)\b/i.test(s || "");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderBody(body) {
  if (!body || !body.trim()) return null;
  if (looksLikeHtml(body)) return { __html: body };
  const html = body
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
  return { __html: html };
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function MyMaterialDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/account/materials/${id}`);
      setMaterial(data);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream" data-testid="my-material-detail-loading">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="h-6 w-24 bg-sand/40 rounded animate-pulse" />
          <div className="h-10 w-3/4 bg-sand/40 rounded animate-pulse mt-4" />
          <div className="h-64 bg-sand/30 rounded-3xl animate-pulse mt-6" />
        </div>
      </div>
    );
  }

  if (notFound || !material) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5" data-testid="my-material-detail-notfound">
        <p className="font-serif text-2xl text-espresso">Материал не найден</p>
        <p className="text-sm text-muted-foreground mt-2 text-center">
          Возможно, он был удалён или доступен только его автору.
        </p>
        <Link to="/account?tab=my-materials" className="chip bg-espresso text-white mt-6 px-5 py-2">
          В мои материалы
        </Link>
      </div>
    );
  }

  const body = renderBody(material.body);

  return (
    <div className="min-h-screen bg-cream" data-testid="my-material-detail-page">
      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button
          onClick={() => nav("/account?tab=my-materials")}
          data-testid="my-material-back-button"
          className="inline-flex items-center gap-1 -ml-1 pl-1 pr-3 py-1.5 rounded-full hover:bg-sand/50 text-espresso"
          aria-label="Назад к моим материалам"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Назад к моим материалам</span>
        </button>
        <div className="ml-auto text-[10px] uppercase tracking-[0.28em] text-muted-foreground flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Приватный · {UMT_LABEL[material.material_type] || material.material_type}
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 pt-4 pb-16">
        <h1 className="font-serif text-4xl leading-tight text-espresso" data-testid="my-material-title">
          {material.title || "Без названия"}
        </h1>
        <div className="text-xs text-muted-foreground mt-2" data-testid="my-material-dates">
          Создано {formatDate(material.created_at)}
          {material.updated_at && material.updated_at !== material.created_at && (
            <> · обновлено {formatDate(material.updated_at)}</>
          )}
        </div>
        {material.short_description && (
          <p className="text-lg text-muted-foreground mt-3" data-testid="my-material-short">
            {material.short_description}
          </p>
        )}

        {material.cover_image && (
          <figure className="mt-6" data-testid="my-material-cover-figure">
            <img
              src={material.cover_image}
              alt={material.title || ""}
              className="block mx-auto max-w-full h-auto max-h-[70vh] rounded-3xl"
              data-testid="my-material-cover-image"
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
          </figure>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => nav(`/account?tab=my-materials&edit=${material.id}`)}
            data-testid="my-material-edit-button"
            className="chip px-5 py-2 border bg-espresso text-white border-espresso hover:bg-espressoDark transition-colors flex items-center gap-2"
          >
            <Pencil className="w-4 h-4" />
            Редактировать
          </button>
        </div>

        {material.tags?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="my-material-tags">
            {material.tags.map((t, i) => (
              <span key={`${t}-${i}`} className="chip bg-white border border-[#E1D2B8] text-espresso px-3 py-1 text-xs">
                #{t}
              </span>
            ))}
          </div>
        )}

        {body ? (
          <div
            className="anokina-content font-serif text-espresso mt-8 leading-relaxed text-[17px] whitespace-pre-wrap"
            data-testid="my-material-body"
            dangerouslySetInnerHTML={body}
          />
        ) : (
          <p className="font-serif italic text-espresso/60 mt-8" data-testid="my-material-body-empty">
            Содержание пока не заполнено. Нажмите «Редактировать», чтобы добавить.
          </p>
        )}
      </article>
    </div>
  );
}
